import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useDateFormat } from '../contexts/DateFormatContext';
import api from '../services/api';
import './AccountSettings.css';

interface CurrencySetting {
  id?: string;
  currencyCode: string;
  currencySymbol: string;
  currencyName: string;
}

interface AccountSettingsProps {
  onClose?: () => void;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const { dateFormat, setDateFormat } = useDateFormat();
  
  const [currencySetting, setCurrencySetting] = useState<CurrencySetting>({
    currencyCode: 'JPY',
    currencySymbol: '¥',
    currencyName: t('currency:currencies.JPY')
  });
  
  const [selectedLanguage, setSelectedLanguage] = useState<string>(i18n.language);
  const [selectedDateFormat, setSelectedDateFormat] = useState<string>(dateFormat);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  const languages = [
    { code: 'ja', name: t('common:japanese'), flag: '🇯🇵' },
    { code: 'en', name: t('common:english'), flag: '🇺🇸' },
    { code: 'th', name: t('common:thai'), flag: '🇹🇭' },
  ];

  const CURRENCY_OPTIONS = [
    { code: 'JPY', symbol: '¥', name: t('currency:currencies.JPY') },
    { code: 'THB', symbol: '฿', name: t('currency:currencies.THB') },
    { code: 'USD', symbol: '$', name: t('currency:currencies.USD') },
    { code: 'EUR', symbol: '€', name: t('currency:currencies.EUR') },
  ];

  const DATE_FORMAT_OPTIONS = [
    { code: 'dd-mm-yyyy', name: 'DD-MM-YYYY' },
    { code: 'mm-dd-yyyy', name: 'MM-DD-YYYY' },
    { code: 'yyyy-mm-dd', name: 'YYYY-MM-DD' },
  ];

  useEffect(() => {
    if (user && token) {
      fetchSettings();
    }
  }, [user, token]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/currency-settings');
      setCurrencySetting(response.data);
    } catch (error) {
      console.error(t('currency:fetchError'), error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (languageCode: string) => {
    setSelectedLanguage(languageCode);
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCurrency = CURRENCY_OPTIONS.find(c => c.code === e.target.value);
    if (selectedCurrency) {
      setCurrencySetting({
        ...currencySetting,
        currencyCode: selectedCurrency.code,
        currencySymbol: selectedCurrency.symbol,
        currencyName: selectedCurrency.name
      });
    }
  };

  const handleSave = async () => {
    if (!user || !token) return;

    setIsSaving(true);
    setMessage('');

    try {
      // Save currency settings
      await api.post('/currency-settings', currencySetting);
      
      // Change language
      if (selectedLanguage !== i18n.language) {
        await i18n.changeLanguage(selectedLanguage);
      }
      
      // Update date format
      if (selectedDateFormat !== dateFormat) {
        setDateFormat(selectedDateFormat);
      }
      
      setMessage(t('account:settingsSaved'));
      
      // Close the dropdown after saving
      if (onClose) {
        setTimeout(() => {
          onClose();
          // Refresh the page to update currency display throughout the app
          window.location.reload();
        }, 1000);
      } else {
        // Refresh the page to update currency display throughout the app
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error: any) {
      console.error('Settings save error:', error);
      const errorMessage = error.response?.data?.error || t('account:saveFailed');
      setMessage(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="account-settings-loading">{t('account:loadingSettings')}</div>;
  }

  return (
    <div className="account-settings">
      <h2>{t('account:accountSettings')}</h2>
      
      <div className="settings-form">
        {/* Language Settings Section */}
        <div className="settings-section">
          <h3>{t('account:languageSettings')}</h3>
          <div className="form-group">
            <label htmlFor="language-select">{t('account:selectLanguage')}</label>
            <select
              id="language-select"
              value={selectedLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
              disabled={isSaving}
              className="settings-select"
            >
              {languages.map(language => (
                <option key={language.code} value={language.code}>
                  {language.flag} {language.name}
                </option>
              ))}
            </select>
            <div className="current-setting">
              <span className="setting-label">{t('account:currentLanguage')}</span>
              <span className="setting-value">
                {languages.find(l => l.code === i18n.language)?.flag} {languages.find(l => l.code === i18n.language)?.name}
              </span>
            </div>
          </div>
        </div>

        {/* Currency Settings Section */}
        <div className="settings-section">
          <h3>{t('account:currencySettings')}</h3>
          <div className="form-group">
            <label htmlFor="currency-select">{t('account:selectCurrency')}</label>
            <select
              id="currency-select"
              value={currencySetting.currencyCode}
              onChange={handleCurrencyChange}
              disabled={isSaving}
              className="settings-select"
            >
              {CURRENCY_OPTIONS.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.symbol} {currency.name} ({currency.code})
                </option>
              ))}
            </select>
            <div className="current-setting">
              <span className="setting-label">{t('account:currentCurrency')}</span>
              <span className="setting-value">
                {currencySetting.currencySymbol} {currencySetting.currencyName} ({currencySetting.currencyCode})
              </span>
            </div>
          </div>
        </div>

        {/* Date Format Settings Section */}
        <div className="settings-section">
          <h3>{t('account:dateFormatSettings')}</h3>
          <div className="form-group">
            <label htmlFor="date-format-select">{t('account:selectDateFormat')}</label>
            <select
              id="date-format-select"
              value={selectedDateFormat}
              onChange={(e) => setSelectedDateFormat(e.target.value)}
              disabled={isSaving}
              className="settings-select"
            >
              {DATE_FORMAT_OPTIONS.map(format => (
                <option key={format.code} value={format.code}>
                  {format.name}
                </option>
              ))}
            </select>
            <div className="current-setting">
              <span className="setting-label">{t('account:currentDateFormat')}</span>
              <span className="setting-value">
                {DATE_FORMAT_OPTIONS.find(f => f.code === selectedDateFormat)?.name}
              </span>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="form-actions">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="save-button"
          >
            {isSaving ? t('common:saving') : t('account:saveSettings')}
          </button>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`message ${message.includes(t('account:saveFailed')) ? 'error' : 'success'}`}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSettings;